import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface MenuItem {
  label: string;
  action?: () => void;
  number?: string;
}

interface ServiceItem {
  href: string;
  icon: string;
  label: string;
  sub: string;
}

const StaggeredMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [hasOpened, setHasOpened] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      setHasOpened(true);
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  const scrollToSection = (sectionId: string) => {
    setIsOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) section.scrollIntoView({ behavior: 'smooth' });
      }, 700);
    } else {
      setTimeout(() => {
        const section = document.getElementById(sectionId);
        if (section) section.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  };

  const scrollToTop = () => {
    setIsOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 500);
    }
  };

  const handleToggle = () => {
    if (animating) return;
    setAnimating(true);
    setIsOpen((prev) => !prev);
    setTimeout(() => setAnimating(false), 800);
  };

  const mainItems: MenuItem[] = [
    { label: 'Home', number: '01', action: scrollToTop },
    { label: 'Über uns', number: '02', action: () => scrollToSection('about') },
    { label: 'Portfolio', number: '03', action: () => { setIsOpen(false); navigate('/portfolio'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 50); } },
    { label: 'Prozess', number: '04', action: () => scrollToSection('process') },
    { label: 'Kontakt', number: '05', action: () => { setIsOpen(false); navigate('/kontakt'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 50); } },
  ];

  const services: ServiceItem[] = [
    { href: '/webdesign-entwicklung', icon: 'ri-code-s-slash-line', label: 'Webdesign & Entwicklung', sub: 'Moderne Websites' },
    { href: '/social-media', icon: 'ri-instagram-line', label: 'Social Media Marketing', sub: 'Content & Community' },
    { href: '/foto-videographie', icon: 'ri-camera-line', label: 'Foto & Videographie', sub: 'Professionelle Aufnahmen' },
  ];

  const isLight = isScrolled && !isOpen;

  return (
    <>
      <style>{`
        @keyframes curtainIn {
          0% { clip-path: polygon(0 0, 100% 0, 100% 0, 0 0); }
          100% { clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%); }
        }
        @keyframes curtainOut {
          0% { clip-path: polygon(0 0, 100% 0, 100% 100%, 0 100%); }
          100% { clip-path: polygon(0 100%, 100% 100%, 100% 100%, 0 100%); }
        }
        @keyframes slideUpFade {
          0% { opacity: 0; transform: translateY(40px) skewY(3deg); }
          100% { opacity: 1; transform: translateY(0) skewY(0deg); }
        }
        @keyframes slideDownFade {
          0% { opacity: 1; transform: translateY(0) skewY(0deg); }
          100% { opacity: 0; transform: translateY(-30px) skewY(-2deg); }
        }
        @keyframes fadeSlideRight {
          0% { opacity: 0; transform: translateX(30px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes fadeSlideLeft {
          0% { opacity: 1; transform: translateX(0); }
          100% { opacity: 0; transform: translateX(20px); }
        }
        @keyframes lineExpand {
          0% { transform: scaleX(0); }
          100% { transform: scaleX(1); }
        }
        @keyframes noiseShift {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(-1px, 1px); }
          50% { transform: translate(1px, -1px); }
          75% { transform: translate(-1px, -1px); }
        }
        @keyframes serviceGlow {
          0%, 100% { box-shadow: 0 0 0px rgba(201,168,76,0); }
          50% { box-shadow: 0 0 20px rgba(201,168,76,0.15); }
        }
        .service-card {
          position: relative;
          overflow: hidden;
          transition: all 0.35s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .service-card::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(201,168,76,0.08) 0%, transparent 60%);
          opacity: 0;
          transition: opacity 0.35s ease;
          border-radius: 12px;
        }
        .service-card:hover::before {
          opacity: 1;
        }
        .service-card:hover {
          transform: translateX(6px);
          background: rgba(255,255,255,0.04);
          border-color: rgba(201,168,76,0.25) !important;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .service-shimmer::after {
          content: '';
          position: absolute;
          top: 0; left: 0;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(201,168,76,0.08), transparent);
          transform: translateX(-100%);
        }
        .service-card:hover .service-shimmer::after {
          animation: shimmer 0.6s ease forwards;
        }
        .menu-overlay {
          animation: curtainIn 0.75s cubic-bezier(0.77, 0, 0.175, 1) forwards;
        }
        .menu-overlay-out {
          animation: curtainOut 0.65s cubic-bezier(0.77, 0, 0.175, 1) forwards;
        }
        .nav-item-in {
          animation: slideUpFade 0.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .nav-item-out {
          animation: slideDownFade 0.35s cubic-bezier(0.55, 0, 1, 0.45) forwards;
        }
        .side-in {
          animation: fadeSlideRight 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        .side-out {
          animation: fadeSlideLeft 0.3s ease-in forwards;
        }
        .divider-in {
          animation: lineExpand 0.5s cubic-bezier(0.22, 1, 0.36, 1) forwards;
          transform-origin: left;
        }
        .menu-char {
          display: inline-block;
          transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), color 0.3s ease;
        }
        .nav-btn:hover .menu-char {
          color: #c9a84c;
        }
        .nav-btn:hover .menu-char:nth-child(odd) {
          transform: translateY(-4px);
        }
        .nav-btn:hover .menu-char:nth-child(even) {
          transform: translateY(4px);
        }
        .noise-bg::before {
          content: '';
          position: absolute;
          inset: -50%;
          width: 200%;
          height: 200%;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
          opacity: 0.04;
          pointer-events: none;
          animation: noiseShift 0.15s steps(1) infinite;
        }
        .gold-line {
          position: absolute;
          left: 0;
          bottom: 0;
          height: 1px;
          width: 100%;
          background: linear-gradient(90deg, #c9a84c, transparent);
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.4s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .nav-btn:hover .gold-line {
          transform: scaleX(1);
        }
      `}</style>

      {/* Fixed Header Bar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isOpen
            ? 'bg-transparent'
            : isScrolled
            ? 'bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm'
            : 'bg-transparent'
        }`}
      >
        <div className="px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">

            {/* Logo */}
            <button
              onClick={scrollToTop}
              className="flex items-center group cursor-pointer relative z-[60]"
              aria-label="Home"
            >
              <img
                src="https://public.readdy.ai/ai/img_res/d52d164b-8720-4903-912c-89760fb1836e.png"
                alt="Bamme Media Logo"
                className={`h-14 w-auto object-contain transition-all duration-300 ${
                  isOpen ? 'brightness-0 invert' : isLight ? 'brightness-0' : 'brightness-0 invert'
                }`}
              />
            </button>

            {/* Hamburger / Close Button */}
            <button
              onClick={handleToggle}
              className="relative z-[60] flex flex-col items-center justify-center w-12 h-12 cursor-pointer gap-1.5 group"
              aria-label="Toggle menu"
            >
              <span
                className={`block h-0.5 transition-all duration-500 ease-in-out ${
                  isOpen ? 'w-6 rotate-45 translate-y-[7px] bg-white' : isLight ? 'w-6 bg-black' : 'w-6 bg-white'
                }`}
              ></span>
              <span
                className={`block h-0.5 transition-all duration-300 ease-in-out ${
                  isOpen ? 'w-0 opacity-0 bg-white' : isLight ? 'w-4 bg-black' : 'w-4 bg-white'
                }`}
              ></span>
              <span
                className={`block h-0.5 transition-all duration-500 ease-in-out ${
                  isOpen ? 'w-6 -rotate-45 -translate-y-[7px] bg-white' : isLight ? 'w-6 bg-black' : 'w-6 bg-white'
                }`}
              ></span>
            </button>
          </div>
        </div>
      </header>

      {/* Full-Screen Overlay Menu */}
      {(isOpen || hasOpened) && (
        <div
          ref={menuRef}
          className={`fixed inset-0 z-40 noise-bg overflow-hidden ${
            isOpen ? 'menu-overlay' : 'menu-overlay-out'
          }`}
          style={{
            background: 'linear-gradient(135deg, #0a0a0a 0%, #141414 40%, #0f0f0f 100%)',
            clipPath: isOpen ? undefined : undefined,
          }}
        >
          {/* Gold accent blobs */}
          <div
            className="absolute pointer-events-none"
            style={{
              top: '-10%', right: '-5%',
              width: '500px', height: '500px',
              background: 'radial-gradient(circle, rgba(201,168,76,0.07) 0%, transparent 70%)',
              filter: 'blur(40px)',
            }}
          ></div>
          <div
            className="absolute pointer-events-none"
            style={{
              bottom: '-10%', left: '-5%',
              width: '400px', height: '400px',
              background: 'radial-gradient(circle, rgba(201,168,76,0.04) 0%, transparent 70%)',
              filter: 'blur(60px)',
            }}
          ></div>

          {/* Large decorative number */}
          <div
            className="absolute select-none pointer-events-none"
            style={{
              right: '5%', top: '50%', transform: 'translateY(-50%)',
              fontSize: 'clamp(200px, 25vw, 380px)',
              fontWeight: 900,
              color: 'rgba(255,255,255,0.015)',
              lineHeight: 1,
              letterSpacing: '-0.05em',
              fontFamily: 'sans-serif',
            }}
          >
            BM
          </div>

          <div className="relative h-full flex flex-col lg:flex-row">
            {/* Left: Main Nav Items */}
            <div className="flex-1 flex flex-col justify-center px-10 lg:px-20 pt-28 lg:pt-0">

              {/* Small label */}
              <div
                className={isOpen ? 'side-in' : 'side-out'}
                style={{ animationDelay: isOpen ? '200ms' : '0ms', opacity: 0, animationFillMode: 'forwards' }}
              >
                <p className="text-xs font-semibold text-white/20 uppercase tracking-[0.3em] mb-8">
                  Navigation
                </p>
              </div>

              <nav className="space-y-0">
                {mainItems.map((item, i) => (
                  <div key={item.label}>
                    <div
                      className={isOpen ? 'nav-item-in' : 'nav-item-out'}
                      style={{
                        animationDelay: isOpen ? `${i * 70 + 250}ms` : `${(mainItems.length - 1 - i) * 40}ms`,
                        opacity: 0,
                        animationFillMode: 'forwards',
                      }}
                    >
                      <button
                        onClick={item.action}
                        className="nav-btn group relative flex items-baseline gap-5 py-4 cursor-pointer w-full text-left overflow-hidden"
                      >
                        <span className="text-xs font-mono text-white/20 group-hover:text-[#c9a84c] transition-colors duration-400 w-6 shrink-0 mb-1">
                          {item.number}
                        </span>
                        <span
                          className="text-5xl lg:text-7xl font-black text-white/85 tracking-tight leading-none"
                          style={{ fontFamily: "'Inter', sans-serif" }}
                        >
                          {item.label.split('').map((char, ci) => (
                            <span key={ci} className="menu-char" style={{ transitionDelay: `${ci * 20}ms` }}>
                              {char === ' ' ? '\u00A0' : char}
                            </span>
                          ))}
                        </span>
                        <span className="ml-auto opacity-0 group-hover:opacity-100 transition-all duration-300 -translate-x-4 group-hover:translate-x-0 transform shrink-0">
                          <i className="ri-arrow-right-up-line text-2xl text-[#c9a84c]"></i>
                        </span>
                        <span className="gold-line"></span>
                      </button>
                    </div>
                    {i < mainItems.length - 1 && (
                      <div
                        className={isOpen ? 'divider-in' : ''}
                        style={{
                          height: '1px',
                          background: 'rgba(255,255,255,0.06)',
                          animationDelay: isOpen ? `${i * 70 + 300}ms` : '0ms',
                          animationFillMode: 'forwards',
                          transform: isOpen ? undefined : 'scaleX(1)',
                        }}
                      ></div>
                    )}
                  </div>
                ))}
              </nav>
            </div>

            {/* Right: Services + Social */}
            <div
              className={`lg:w-[32rem] xl:w-[38rem] flex flex-col justify-center px-10 lg:px-16 pb-16 lg:pb-0 pt-8 lg:pt-0 border-t lg:border-t-0 lg:border-l border-white/8 ${
                isOpen ? 'side-in' : 'side-out'
              }`}
              style={{
                animationDelay: isOpen ? '500ms' : '0ms',
                opacity: 0,
                animationFillMode: 'forwards',
              }}
            >
              {/* Services */}
              <div className="mb-10">
                <div className="flex items-center gap-3 mb-8">
                  <div className="h-px flex-1 bg-gradient-to-r from-[#c9a84c]/40 to-transparent"></div>
                  <p className="text-sm font-bold text-[#c9a84c]/80 uppercase tracking-[0.3em]">
                    Services
                  </p>
                  <div className="h-px flex-1 bg-gradient-to-l from-[#c9a84c]/40 to-transparent"></div>
                </div>
                <div className="space-y-4">
                  {services.map((s, i) => (
                    <a
                      key={s.href}
                      href={s.href}
                      onClick={() => setIsOpen(false)}
                      className="service-card service-shimmer flex items-center gap-5 py-5 px-5 rounded-2xl border border-white/10 cursor-pointer"
                      style={{
                        animationDelay: isOpen ? `${i * 60 + 550}ms` : '0ms',
                        background: 'rgba(255,255,255,0.04)',
                      }}
                    >
                      <div className="w-16 h-16 flex items-center justify-center rounded-xl shrink-0 relative"
                        style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.22), rgba(201,168,76,0.08))', border: '1px solid rgba(201,168,76,0.3)' }}>
                        <i className={`${s.icon} text-2xl text-[#c9a84c]`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-lg font-bold text-white/90 group-hover:text-white transition-colors duration-300 truncate">
                          {s.label}
                        </div>
                        <div className="text-sm text-white/45 mt-1">{s.sub}</div>
                      </div>
                      <div className="w-9 h-9 flex items-center justify-center rounded-full shrink-0"
                        style={{ background: 'rgba(201,168,76,0.12)', border: '1px solid rgba(201,168,76,0.25)' }}>
                        <i className="ri-arrow-right-s-line text-xl text-[#c9a84c]/80"></i>
                      </div>
                    </a>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div className="h-px bg-white/8 mb-8"></div>

              {/* CTA */}
              <button
                onClick={() => { setIsOpen(false); navigate('/kontakt'); setTimeout(() => window.scrollTo({ top: 0, behavior: 'instant' }), 50); }}
                className="w-full py-4 rounded-xl font-bold text-base text-black transition-all duration-300 hover:scale-[1.03] hover:shadow-lg whitespace-nowrap cursor-pointer mb-8 relative overflow-hidden group"
                style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c96a)' }}
              >
                <span className="relative z-10">Jetzt starten →</span>
                <div className="absolute inset-0 bg-white/0 group-hover:bg-white/10 transition-all duration-300"></div>
              </button>

              {/* Social */}
              <div>
                <p className="text-xs font-semibold text-white/25 uppercase tracking-[0.25em] mb-4">
                  Social Media
                </p>
                <div className="flex gap-3">
                  {[
                    { icon: 'ri-instagram-line', href: 'https://www.instagram.com/bamme.media/', label: 'Instagram' },
                    { icon: 'ri-facebook-line', href: 'https://www.facebook.com/bamme.media/', label: 'Facebook' },
                  ].map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      aria-label={s.label}
                      className="w-11 h-11 flex items-center justify-center rounded-lg bg-white/5 hover:bg-[#c9a84c]/20 border border-white/5 hover:border-[#c9a84c]/30 text-white/35 hover:text-[#c9a84c] transition-all duration-300 cursor-pointer hover:scale-110"
                    >
                      <i className={`${s.icon} text-xl`}></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StaggeredMenu;
